import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CustomEditFieldComponent, Form, FormMode } from '../../../../../../base/form';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject, defer, EMPTY, Observable, Subscription } from 'rxjs';
import {
  arr_filter,
  arr_last,
  arr_map,
  blank,
  data_get,
  data_is_false,
  fallback,
  filled,
  generateId,
  Identifiable,
  observe,
  Omit,
  OptionalValue,
  spf,
  transform,
} from '../../../../../../shared/utils/common';
import { ApiService } from '../../../../../../services/api.service';
import { AssetTypeVariant } from '../../../types';
import { FormService, StudioFormTab } from '../../../../../../services/form.service';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { NotificationService } from '../../../../../../services/notification.service';
import { DIALOG_DATA, DialogService } from '../../../../../../services/dialog.service';
import { RelateOption } from '../../../../../../shared/components/forms/input/relate/relate-input.component';

@Component({
  selector: 'asset-types-variants-input',
  template: `
    <div class="d-flex d-flex-gap-2x flex-column">
      <div fieldmagicGridRow>
        <div
          fieldmagicGridColumn="12"
          class="d-flex d-flex-gap justify-content-end"
        >
          <fieldmagic-primary-button
            label="Add Variant"
            [icon]="[
              {
                icon: 'plus-circle',
                klass: 'text-success'
              }
            ]"
            purpose="icon"
            (click)="onAdd()"
          >
          </fieldmagic-primary-button>
          <fieldmagic-primary-button
            purpose="ai"
            [isInProgress]="generating$ | async"
            (click)="onGenerate()"
          >
          </fieldmagic-primary-button>
        </div>
      </div>

      <div fieldmagicGridRow>
        <div fieldmagicGridColumn="12">
          <table class="table table-bordered">
            <thead>
              <tr>
                <td width="40%">
                  <fieldmagic-text
                    content="Variant"
                    purpose="table-header"
                    [withRequiredMarker]="true"
                  >
                  </fieldmagic-text>
                </td>
                <td width="35%">
                  <fieldmagic-text
                    content="Description"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td width="25%">
                  <fieldmagic-text
                    content="Linked Product"
                    purpose="table-header"
                  >
                  </fieldmagic-text>
                </td>
                <td width="5%"></td>
              </tr>
            </thead>

            <tbody>
              <ng-container *ngIf="fetching$ | async">
                <tr class="text-center">
                  <td colspan="3">
                    <fieldmagic-icon [spin]="true"></fieldmagic-icon>
                  </td>
                </tr>
              </ng-container>

              <ng-container *ngIf="! (fetching$ | async)">
                <ng-container *ngIf="variants$ | async as variants">
                  <tr
                    *ngIf="isEmpty"
                    class="text-center"
                  >
                    <td colspan="3">{{ 'No variants available.' | translate }}</td>
                  </tr>

                  <ng-container *ngFor="let variant of variants; let variant_index = index; trackBy: tracker">
                    <ng-container *ngIf="! variant.deleted">
                      <tr>
                        <td>
                          <div class="d-flex d-flex-gap align-items-center">
                            <fieldmagic-primary-button
                              purpose="add-line"
                              (click)="onAddSubVariant(variant_index)"
                              tooltip="Click to add sub variant"
                            >
                            </fieldmagic-primary-button>

                            <fieldmagic-text-input
                              placeholder="Variant Name.."
                              [standalone]="true"
                              [(ngModel)]="variant.name"
                              [errors]="errors | data_get: ('%s:name' | sprintf: [variant.id])"
                              (ngModelChange)="onVariantChange(variant_index)"
                              class="w-100"
                            >
                            </fieldmagic-text-input>
                          </div>
                        </td>
                        <td>
                          <fieldmagic-textarea-input
                            [expandable]="true"
                            placeholder="Description..."
                            [standalone]="true"
                            [(ngModel)]="variant.description"
                            [errors]="errors | data_get: ('%s:description' | sprintf: [variant.id])"
                            (ngModelChange)="onVariantChange(variant_index)"
                          >
                          </fieldmagic-textarea-input>
                        </td>
                        <td>
                          <fieldmagic-relate-input
                            placeholder="Please select a product linked to this variant.."
                            [(ngModel)]="variant.item_id"
                            moduleName="items"
                            [filter]="{
                              active: true,
                              labor: false
                            }"
                            (ngModelChange)="onVariantChange(variant_index)"
                          >
                          </fieldmagic-relate-input>
                        </td>
                        <td>
                          <div class="d-flex d-flex-gap">
                            <fieldmagic-primary-button
                              *ngIf="isLast(variant)"
                              purpose="add-line"
                              (click)="onAdd()"
                              tooltip="Click to add variant"
                            >
                            </fieldmagic-primary-button>

                            <fieldmagic-primary-button
                              purpose="delete-icon"
                              (click)="onRemove(variant_index)"
                              tooltip="Click to remove variant"
                            >
                            </fieldmagic-primary-button>
                          </div>
                        </td>
                      </tr>

                      <ng-container *ngIf="variant.sub_variants | filled">
                        <ng-container *ngFor="let sub_variant of variant.sub_variants; let sub_variant_index = index; trackBy: tracker">
                          <tr *ngIf="! sub_variant.deleted">
                            <td>
                              <fieldmagic-indent>
                                <fieldmagic-text-input
                                  placeholder="Variant Name.."
                                  [standalone]="true"
                                  [(ngModel)]="sub_variant.name"
                                  [errors]="errors | data_get: ('%s:%s:name' | sprintf: [variant.id, sub_variant.id])"
                                  (ngModelChange)="onVariantChange(variant_index)"
                                  class="w-100"
                                >
                                </fieldmagic-text-input>
                              </fieldmagic-indent>
                            </td>
                            <td>
                              <fieldmagic-textarea-input
                                [expandable]="true"
                                placeholder="Description..."
                                [standalone]="true"
                                [(ngModel)]="sub_variant.description"
                                [errors]="errors | data_get: ('%s:%s:description' | sprintf: [variant.id, sub_variant.id])"
                                (ngModelChange)="onVariantChange(variant_index)"
                              >
                              </fieldmagic-textarea-input>
                            </td>
                            <td>
                              <fieldmagic-relate-input
                                placeholder="Please select a product linked to this variant.."
                                [(ngModel)]="sub_variant.item_id"
                                moduleName="items"
                                [filter]="{
                                  active: true,
                                  labor: false
                                }"
                                (ngModelChange)="onVariantChange(variant_index)"
                              >
                              </fieldmagic-relate-input>
                            </td>
                            <td>
                              <fieldmagic-primary-button
                                purpose="delete-icon"
                                tooltip="Click to remove sub variant"
                                (click)="onSubVariantRemove(variant_index, sub_variant_index)"
                              >
                              </fieldmagic-primary-button>
                            </td>
                          </tr>
                        </ng-container>
                      </ng-container>
                    </ng-container>
                  </ng-container>
                </ng-container>
              </ng-container>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `,
  providers: [
    DialogService,
  ],
})
export class AssetTypeVariantsInputComponent implements CustomEditFieldComponent, OnInit, OnDestroy {
  errors: Record<string, string[]> = {};

  get isEmpty(): boolean {
    const variants = this.variants$.value;

    return blank(arr_filter(variants, {
      predicate: (variant) => ! variant.deleted,
    }));
  }

  readonly fetching$ = new BehaviorSubject<boolean>(true);
  readonly generating$ = new BehaviorSubject<boolean>(false);

  readonly variants$ = new BehaviorSubject<EditableAssetTypeVariant[]>([]);

  private readonly _mode$ = new BehaviorSubject<FormMode>('add');
  private readonly _field$ = new BehaviorSubject<OptionalValue<Form<any>>>(null);
  private readonly _parent$ = new BehaviorSubject<OptionalValue<FormGroup>>(null);
  private readonly _data$ = new BehaviorSubject<AssetTypeVariantsInputData>({});
  private readonly _name$ = new BehaviorSubject<OptionalValue<string>>(null);
  private readonly _applicableStandard$ = new BehaviorSubject<OptionalValue<string>>(null);

  private _fetchingSubscription?: Subscription;
  private _onNameFieldChangeSubscription?: Subscription;
  private _onApplicationStandardsFieldChangeSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _forms: FormService,
    private readonly _notifications: NotificationService,
    private readonly _dialog: DialogService,
  ) {}

  ngOnInit(): void {
    if (this._mode$.value == 'edit') {
      this._fetchingSubscription = observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => this._api.callPrivate$<AssetTypeVariant[]>({
          path: '/asset_types/variants',
          action: 'post',
          expectsNewFormat: true,
          body: {
            'asset_type_id': data_get(this._data$.value, 'record_id'),
          },
          onSuccessResponse: (response) => arr_map(response.get('items'), {
            transformer: (variant) => ({
              ... variant,
              item_id: transform(data_get(variant, 'item_text'), {
                transformer: (text) => ({
                  id: data_get(variant, 'item_id'),
                  text,
                })
              }),
              sub_variants: arr_map(data_get(variant, 'sub_variants'), {
                transformer: (subVariant) => ({
                  ... subVariant,
                  item_id: transform(data_get(subVariant, 'item_text'), {
                    transformer: (text) => ({
                      id: data_get(subVariant, 'item_id'),
                      text,
                    }),
                  }),
                })
              }),
            }),
          }),
        })
      })
      .pipe(
        filter((variants) => filled(variants)),
      )
      .subscribe((variants) => this.variants$.next(variants));
    } else {
      this.fetching$.next(false);
    }

    this._listenToFieldChanges();

    this.variants$.subscribe((variants) => this._parent$.value.setValue({
      variants: arr_map(variants, {
        transformer: (variant) => ({
          ... variant,
          item_id: data_get(variant, 'item_id.id'),
          sub_variants: arr_map(data_get(variant, 'sub_variants'), {
            transformer: (sub) => ({
              ... sub,
              item_id: data_get(sub, 'item_id.id'),
            }),
          }),
        }),
      }),
    }));
  }

  ngOnDestroy(): void {
    if (this._fetchingSubscription) {
      this._fetchingSubscription.unsubscribe();
    }

    if (this._onNameFieldChangeSubscription) {
      this._onNameFieldChangeSubscription.unsubscribe();
    }

    if (this._onApplicationStandardsFieldChangeSubscription) {
      this._onApplicationStandardsFieldChangeSubscription.unsubscribe();
    }
  }

  /**
   * @inheritdoc
   */
  setFormMode = (mode: FormMode) => this._mode$.next(mode);

  /**
   * @inheritdoc
   */
  setParentForm = (parent: FormGroup) => this._parent$.next(parent);

  /**
   * @inheritdoc
   */
  setField = (field: Form<any>) => this._field$.next(field);

  setAdditionalData = (data: AssetTypeVariantsInputData) => this._data$.next(data);

  onGenerate = () => observe({
    before: () => this.generating$.next(true),
    after: () => this.generating$.next(false),
    observable: () => defer(() => {
      if (blank(this._name$.value)) {
        this._notifications.notifyError('Please set the name of the asset type in order to generate variants');
        return EMPTY;
      }

      const dialog$ = new Observable<AssetTypeVariantSpecificsDialogResult>((observer) => {
        this._dialog.show<AssetTypeVariantSpecificsDialogComponent, AssetTypeVariantSpecificsDialogData>({
          component: AssetTypeVariantSpecificsDialogComponent,
          data: {
            name: this._name$.value,
            applicable_standards: this._applicableStandard$.value,
          },
          afterClosing: (results) => {
            observer.next(results)
          },
        })
      });

      if (this._mode$.value == 'edit' && filled(this.variants$.value)) {
        return this._notifications.sendConfirmation('This will override the current variants. Are you sure you want to proceed?')
          .pipe(
            filter((result) => result.answer),
            switchMap(() => dialog$)
          );
      }

      return dialog$;
    }),
  }).pipe(
    filter((variants) => filled(variants)),
    tap(() => this._notifications.notifySuccess('Variants generated successfully.')),
  ).subscribe((variants) => this.variants$.next([
    ... arr_map(this.variants$.value, {
      transformer: (variant) => ({
        ... variant,
        deleted: true,
      })
    }),
    ... variants,
  ]));

  onAdd = () => this.variants$.next([
    ... this.variants$.value,
    {
      id: generateId(),
      name: '',
      item_id: null,
      sub_variants: [],
    },
  ]);

  onRemove(index: number): void {
    const current = this.variants$.value;

    if (index > current.length || index < 0) {
      return;
    }

    const variant = current[index];

    current[index] = {
      ... variant,
      deleted: true,
    };

    this.variants$.next([ ... current ]);
  }

  onVariantChange(_: number): void {
    this.variants$.next([... this.variants$.value]);
  }

  isLast(item: EditableAssetTypeVariant): boolean {
    const last = arr_last(this.variants$.value, {
      predicate: (variant) => data_is_false(variant.deleted),
    });

    return data_get(last, 'id') == data_get(item, 'id');
  }

  onAddSubVariant(index: number): void {
    if (index < 0) {
      return;
    }

    const variants = [... this.variants$.value];

    variants[index] = {
      ... variants[index],
      sub_variants: [
        ... fallback(variants[index].sub_variants, {
          to: () => [],
        }),
        ... [{
          id: generateId(),
          name: null,
          description: null,
          deleted: false,
          asset_type_variant_id: variants[index].id,
          item_id: null,
        }],
      ],
    };

    this.variants$.next(variants);
  }

  onSubVariantRemove(parent: number, child: number): void {
    if (parent < 0 || child < 0) {
      return;
    }

    const variants = [... this.variants$.value];
    const variant = variants[parent];
    const children = fallback(variant.sub_variants, {
      to: () => [],
    });

    children[child] = {
      ... children[child],
      deleted: true,
    };

    variants[parent] = {
      ... variant,
      sub_variants: children,
    };

    this.variants$.next(variants);
  }

  tracker = (index: number, data: Identifiable) => data.id;

  private _listenToFieldChanges(): void {
    const tabs: StudioFormTab[] = fallback(this._forms.tabs, {
      fallback: () => [],
    });

    for (const tab of tabs) {
      for (const field of tab.fields) {
        if (field.key == 'name') {
          /// set the current
          this._name$.next(field.default_value);

          this._onNameFieldChangeSubscription = tab.groups.controls['name'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((value) => this._name$.next(value));
        } else if (field.key == 'applicable_standards') {
          /// set the current
          this._applicableStandard$.next(field.default_value);

          this._onApplicationStandardsFieldChangeSubscription = tab.groups.controls['applicable_standards'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((value) => this._applicableStandard$.next(value));
        }
      }
    }
  }
}

type AssetTypeVariantsInputData = {
  record_id?: string;
}

type EditableAssetTypeVariant = AssetTypeVariant & {
  deleted?: boolean;
  sub_variants?: EditableAssetTypeSubVariant[];
  item_id?: RelateOption;
}

type EditableAssetTypeSubVariant = Omit<AssetTypeVariant, 'sub_variants'> & {
  deleted?: boolean;
  item_id?: RelateOption;
};

@Component({
  selector: 'asset-types-variants-specifics-dialog',
  template: `
    <fieldmagic-dialog
      label="Generate with AI"
      icon="magic"
    >
      <ng-template fieldmagicDialogButtonsTmp>
        <fieldmagic-primary-button
          [isInProgress]="processing$ | async"
          label="Generate"
          icon="magic"
          (click)="onGenerate()"
        >
        </fieldmagic-primary-button>

        <fieldmagic-dialog-close-button
          [instance]="self"
          [disabled]="processing$ | async"
        >
        </fieldmagic-dialog-close-button>
      </ng-template>

      <ng-template fieldmagicDialogContentTmp>
        <fieldmagic-textarea-input
          [standalone]="false"
          label="Tell me more about the variants to be generated (This is optional)."
          [(ngModel)]="specifics"
          placeholder="For example manufacturer, brands etc.."
        >
        </fieldmagic-textarea-input>
      </ng-template>
    </fieldmagic-dialog>
  `,
})
export class AssetTypeVariantSpecificsDialogComponent implements OnInit {
  specifics?: string;

  readonly processing$ = new BehaviorSubject<boolean>(false);

  get self() {
    return this;
  }

  constructor(
    @Inject(DIALOG_DATA) private readonly _data: AssetTypeVariantSpecificsDialogData,
    private readonly _notifications: NotificationService,
    private readonly _api: ApiService,
    private readonly _dialog: DialogService,
  ) {}

  ngOnInit(): void {
    if (filled(this._data.applicable_standards)) {
      this.specifics = spf('Use %s standard', {
        args: [this._data.applicable_standards],
      });
    }
  }

  onGenerate = () => observe({
    before: () => this.processing$.next(true),
    after: () => this.processing$.next(false),
    observable: () => this._api.callPrivate$({
      path: '/asset_types/variants/generate',
      action: 'post',
      headers: {
        'X-Captcha': 'generate_asset_type_variants',
      },
      body: {
        'asset_type_name': this._data.name,
        'specifics': this.specifics,
      },
      expectsNewFormat: true,
      onSuccessResponse: (response) => response.get('items'),
    }),
  }).subscribe((variants) => {
    if (blank(variants)) {
      return this._notifications.notifyError('No variants generated. Please try again with different specifics.');
    }

    return this._dialog.close({
      instance: this,
      result: arr_map(variants, {
        transformer: (variant) => ({
          id: generateId(),
          ... variant,
        })
      }),
    });
  });
}

type AssetTypeVariantSpecificsDialogData = {
  name: string;
  applicable_standards?: string;
}

type AssetTypeVariantSpecificsDialogResult = AssetTypeVariant[];